import { useEffect, useRef, useState } from 'react';
import { api } from '../../api/http';
import { DURATION_PRESETS, type HelixFormField } from '../../domain/helixForm';
import type { ChannelViewer } from '../../domain/types';
import { Avatar, Field, Input, Select, Switch, Textarea } from '../../ui';

export function UserPicker({
  botId,
  channelId,
  value,
  onChange,
  label,
  required,
  hint,
  error,
}: {
  botId: string;
  channelId: string;
  value: string;
  onChange: (userId: string, login?: string) => void;
  label: string;
  required?: boolean;
  hint?: string;
  error?: string;
}): JSX.Element {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<ChannelViewer[]>([]);
  const [picked, setPicked] = useState<ChannelViewer | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!open) return;
    if (timer.current) clearTimeout(timer.current);
    const delay = query.trim().length === 0 ? 0 : 250;
    timer.current = setTimeout(() => {
      void api.searchViewers(botId, channelId, query.trim(), query.trim().length === 0).then(setItems);
    }, delay);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [botId, channelId, open, query]);

  return (
    <Field label={label} required={required} hint={hint} error={error}>
      <div className="tbm-picker">
        <Input
          value={picked ? `${picked.displayName} (@${picked.login})` : query}
          placeholder="Rechercher un spectateur"
          onFocus={() => {
            setOpen(true);
            if (picked) {
              setQuery('');
              setPicked(null);
            }
          }}
          onChange={(e) => {
            setPicked(null);
            setQuery(e.target.value);
            if (value) onChange('');
          }}
        />
        {open ? (
          <ul className="tbm-picker__list">
            {items.map((item) => (
              <li key={item.twitchUserId}>
                <button
                  type="button"
                  className="tbm-picker__item"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    setPicked(item);
                    setQuery('');
                    setOpen(false);
                    onChange(item.twitchUserId, item.login);
                  }}
                >
                  <Avatar src={item.avatarUrl} name={item.displayName || item.login} />
                  <span>
                    <strong>{item.displayName || item.login}</strong>
                    <span className="muted"> @{item.login}</span>
                    {item.present ? <span className="tbm-badge">en direct</span> : null}
                  </span>
                </button>
              </li>
            ))}
            {items.length === 0 ? <li className="muted">Aucun spectateur</li> : null}
          </ul>
        ) : null}
      </div>
    </Field>
  );
}

export function GamePicker({
  botId,
  channelId,
  value,
  onChange,
  label,
  required,
  hint,
}: {
  botId: string;
  channelId: string;
  value: string;
  onChange: (id: string) => void;
  label: string;
  required?: boolean;
  hint?: string;
}): JSX.Element {
  const [query, setQuery] = useState('');
  const [items, setItems] = useState<Array<{ id: string; name: string; box_art_url?: string }>>([]);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (query.trim().length < 2) {
      setItems([]);
      return;
    }
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      void api
        .callHelix(botId, channelId, 'search.getSearchCategories', { query: query.trim() })
        .then((res) => {
          const data = (res.body as { data?: Array<{ id: string; name: string; box_art_url?: string }> })?.data ?? [];
          setItems(data);
        })
        .catch(() => setItems([]));
    }, 250);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [botId, channelId, query]);
  return (
    <Field label={label} required={required} hint={hint}>
      <div className="tbm-picker">
        <Input value={query || value} placeholder="Rechercher une catégorie" onChange={(e) => { onChange(''); setQuery(e.target.value); }} />
        {items.length ? (
          <ul className="tbm-picker__list">
            {items.map((item) => (
              <li key={item.id}>
                <button type="button" className="tbm-picker__item" onClick={() => { onChange(item.id); setQuery(item.name); setItems([]); }}>
                  {item.box_art_url ? <img className="tbm-picker__thumb" src={item.box_art_url.replace('{width}', '45').replace('{height}', '60')} alt="" /> : null}
                  {item.name}
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </Field>
  );
}

export function DurationPicker({
  value,
  onChange,
  permanent,
  onPermanent,
  label,
  hint,
}: {
  value: number | '';
  onChange: (seconds: number | '') => void;
  permanent: boolean;
  onPermanent: (v: boolean) => void;
  label: string;
  hint?: string;
}): JSX.Element {
  return (
    <Field label={label} hint={hint}>
      <Switch checked={permanent} onChange={onPermanent} label="Bannissement définitif" />
      {permanent ? null : (
        <>
          <div className="tbm-preset-row">
            {DURATION_PRESETS.map((preset) => (
              <button
                key={preset.seconds}
                type="button"
                className={`tbm-btn tbm-btn--sm${value === preset.seconds ? ' tbm-btn--primary' : ''}`}
                onClick={() => onChange(preset.seconds)}
              >
                {preset.label}
              </button>
            ))}
          </div>
          <Input
            type="number"
            min={1}
            max={1209600}
            value={value}
            placeholder="Durée en secondes"
            onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
          />
        </>
      )}
    </Field>
  );
}

export function TokenList({
  values,
  onChange,
  label,
  hint,
}: {
  values: string[];
  onChange: (next: string[]) => void;
  label: string;
  hint?: string;
}): JSX.Element {
  const [draft, setDraft] = useState('');
  return (
    <Field label={label} hint={hint}>
      <div className="tbm-scopes">
        {values.map((item, i) => (
          <button key={`${item}-${i}`} type="button" className="tbm-chip" onClick={() => onChange(values.filter((_, j) => j !== i))}>
            {item}
          </button>
        ))}
      </div>
      <Input
        value={draft}
        placeholder="Ajouter puis Entrée"
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            const next = draft.trim();
            if (next) onChange([...values, next]);
            setDraft('');
          }
        }}
      />
    </Field>
  );
}

export function RepeatableList({
  field,
  values,
  onChange,
}: {
  field: HelixFormField;
  values: Array<Record<string, unknown>>;
  onChange: (next: Array<Record<string, unknown>>) => void;
}): JSX.Element {
  const children = field.children ?? [];
  return (
    <Field label={field.name} hint={field.hint} required={field.required}>
      {values.map((row, index) => (
        <div key={index} className="tbm-repeat">
          {children.map((child) => (
            <Input
              key={child.name}
              placeholder={child.name}
              value={String(row[child.name] ?? '')}
              onChange={(e) => {
                const next = values.map((item, i) => (i === index ? { ...item, [child.name]: e.target.value } : item));
                onChange(next);
              }}
            />
          ))}
          <button type="button" className="tbm-btn tbm-btn--sm tbm-btn--ghost" onClick={() => onChange(values.filter((_, i) => i !== index))}>
            Retirer
          </button>
        </div>
      ))}
      <button type="button" className="tbm-btn tbm-btn--sm" onClick={() => onChange([...values, {}])}>
        Ajouter
      </button>
    </Field>
  );
}

export function ScalarField({
  field,
  value,
  onChange,
}: {
  field: HelixFormField;
  value: unknown;
  onChange: (next: unknown) => void;
}): JSX.Element {
  const widget = field.widget ?? 'input';
  if (widget === 'switch') {
    return <Switch checked={Boolean(value)} onChange={(v) => onChange(v)} label={field.name} />;
  }
  if (widget === 'enum') {
    return (
      <Field label={field.name} hint={field.hint} required={field.required}>
        <Select value={String(value ?? '')} onChange={(e) => onChange(e.target.value)}>
          <option value="">—</option>
          {(field.enum ?? []).map((item) => (
            <option key={item} value={item}>{item}</option>
          ))}
        </Select>
      </Field>
    );
  }
  if (widget === 'textarea') {
    const text = String(value ?? '');
    return (
      <Field label={field.name} hint={field.hint} required={field.required}>
        <Textarea value={text} onChange={(e) => onChange(e.target.value)} />
        <span className="muted">{text.length} caractères</span>
      </Field>
    );
  }
  if (widget === 'datetime') {
    return (
      <Field label={field.name} hint={field.hint} required={field.required}>
        <Input type="datetime-local" value={String(value ?? '')} onChange={(e) => onChange(e.target.value)} />
      </Field>
    );
  }
  return (
    <Field label={field.name} hint={field.hint} required={field.required}>
      <Input
        type={field.type === 'number' ? 'number' : 'text'}
        value={value === undefined || value === null ? '' : String(value)}
        onChange={(e) => onChange(field.type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value)}
      />
    </Field>
  );
}
